import React, { useState } from "react";

import { Grid, Paper, Toolbar, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChartOptions } from "chart.js";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { WidgetProps } from "../../../../models/shipment.tracking";
import DashboardService from "../../../../services/dashboard.service";
import { useAppState } from "../../../../context/StateContext";
import { Chart as ChartComponent, ChartType } from "../../../Generic/Chart";
import { textInfoB } from "../../DashStyleGuides";
import NoDataLabel from "../../NoDataLabel";
import useStyles from "./styles";

type Dataset = {
  label: string;
  backgroundColor: string;
  data: number[];
};

const options = {
  maintainAspectRatio: false,
  fill: true,
  pointStyle: "circle",
  pointRadius: 1,
  pointBorderWidth: 1,
  pointBackgroundColor: textInfoB,
  plugins: {
    legend: {
      position: "bottom" as const,
    },
    zoom: {
      zoom: {
        enabled: true,
        mode: "x",
        drag: {
          enabled: true,
        },
        wheel: {
          enabled: true,
        },
      },
    },
  },
};

const ShipmentTrackedSection = ({ widgetFilter }: WidgetProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [data, setData] = useState<any[]>([]);
  const [dashLabelSet, setDashLabelSet] = useState<string[]>([]);

  const isCurrentYear = (year: number): string => {
    const currentYear = new Date().getFullYear();

    if (currentYear === year) return "Current";

    return "Last year";
  };

  const currentBarColor = (year: number): string => {
    if (isCurrentYear(year) === "Current") return "#308CDB";

    return "#E0F2FE";
  };

  useQuery(
    ["dashboard.shipment.tracked"],
    () =>
      DashboardService.getContainerShipmentTracking({
        dateFrom: widgetFilter?.dateFrom,
        dateTo: widgetFilter?.dateTo,
        companyId: currentUser?.companyId,
      }),
    {
      onSuccess: (response) => {
        if (response && response.data) {
          const labels: string[] = [];
          const labelsGroup: string[] = [];
          const dataSets: Dataset[] = [];

          response.data.forEach((item, index) => {
            labelsGroup.push(isCurrentYear(item.year).toUpperCase());
            item.months.forEach((month) => {
              if (!labels.includes(month.month)) {
                labels.push(month.month);
              }
              if (!dataSets[index]) {
                dataSets.push({
                  label: isCurrentYear(item.year).toUpperCase(),
                  backgroundColor: currentBarColor(item.year),
                  data: [month.count],
                });
              } else {
                dataSets[index] = {
                  ...dataSets[index],
                  data: [...dataSets[index].data, month.count],
                };
              }
            });
          });
          setData(dataSets);
          setDashLabelSet(labels);
        }
      },
    }
  );

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.root}>
          <Toolbar className={classes.tblRoot} variant="dense">
            <Typography
              className={classes.title}
              color="textSecondary"
              data-cy="shipmentTrackedTitle"
            >
              {translate("shipmentsTracked")}
            </Typography>
          </Toolbar>
          {data.length === 0 ? (
            <NoDataLabel />
          ) : (
            <div className={classnames("chart-container", classes.orderWrapStyles)}>
              <ChartComponent
                id="chartBarId"
                chartType={ChartType.BAR}
                data={{ labels: dashLabelSet, datasets: data }}
                graphOptions={options as ChartOptions<"bar">}
              />
            </div>
          )}
        </Paper>
      </Grid>
    </React.Fragment>
  );
};

export default ShipmentTrackedSection;
